export type RoomId = string;
export type PlayerId = string;
export type ContentId = string;

export type Mechanic =
  | 'category'
  | 'starts_with_letter'
  | 'contains'
  | 'excludes_letter'
  | 'chain'
  | 'dual_condition'
  | 'name_three';

export const ALL_MECHANICS: readonly Mechanic[] = [
  'category',
  'starts_with_letter',
  'contains',
  'excludes_letter',
  'chain',
  'dual_condition',
  'name_three',
] as const;

export type TensionState = 'normal' | 'acelerado' | 'inminente';

export type GameStateName =
  | 'lobby'
  | 'countdown'
  | 'playing'
  | 'paused'
  | 'exploded'
  | 'between_bombs'
  | 'finished'
  | 'tiebreak_ready'
  | 'tiebreak_playing';

export type VoteChoice = 'valida' | 'no_valida';
export type VoteOutcome = 'valida' | 'no_valida';
export type VotingPhase = 'casting' | 'decided';
export type ReturnTarget = PlayerId | 'no_return';

export interface VotingSnapshot {
  initiatorId: PlayerId;
  votes: Partial<Record<PlayerId, VoteChoice>>;
  phase: VotingPhase;
  outcome: VoteOutcome | null;
  returnDecision: { actorId: PlayerId; target: ReturnTarget } | null;
}

export interface PlayerSnapshot {
  id: PlayerId;
  displayName: string;
  isAdmin: boolean;
  isConnected: boolean;
  joinedAt: number;
  voice?: VoiceState;
}

export interface VoiceState {
  enabled: boolean;
  speaking: boolean;
}

export interface RoomConfig {
  totalBombs: number;
  enabledMechanics: Mechanic[];
  mixedMode: boolean;
  baseDurationMs: number;
}

export const MIN_BASE_DURATION_MS = 10_000;
export const MAX_BASE_DURATION_MS = 60_000;
export const DEFAULT_BASE_DURATION_MS = 30_000;
export const TIME_PRESETS_MS = [10_000, 20_000, 30_000, 40_000] as const;

export const DEFAULT_ROOM_CONFIG: RoomConfig = {
  totalBombs: 5,
  enabledMechanics: [...ALL_MECHANICS],
  mixedMode: false,
  baseDurationMs: DEFAULT_BASE_DURATION_MS,
};

export interface GameStateSnapshot {
  roomId: RoomId;
  state: GameStateName;
  bombNumber: number;
  totalBombs: number;
  currentRound: number;
  currentPlayerId: PlayerId | null;
  rotationOrder: PlayerId[];
  rotationIndex: number;
  lastForcedPassTarget: PlayerId | null;
  remainingTimeMs: number;
  initialTimeMs: number;
  tensionState: TensionState;
  selectedMechanic: Mechanic | null;
  selectedContentId: ContentId | null;
  promptLines: string[];
  paused: boolean;
  pausedBy: PlayerId | null;
  countdownEndsAt: number | null;
  bombEndsAt: number | null;
  returnWindowExpiresAt: number | null;
  returnWindowMsRemaining: number;
  voting: VotingSnapshot | null;
  lastExplosionPlayerId: PlayerId | null;
  tiebreakPlayers: PlayerId[];
  winnerId: PlayerId | null;
  players: PlayerSnapshot[];
  enabledMechanics: Mechanic[];
  mixedMode: boolean;
  baseDurationMs: number;
}

const REQUIRED_GAME_STATE_SNAPSHOT_FIELDS: readonly (keyof GameStateSnapshot)[] = [
  'roomId', 'state', 'bombNumber', 'totalBombs', 'currentRound', 'currentPlayerId',
  'rotationOrder', 'rotationIndex', 'lastForcedPassTarget', 'remainingTimeMs', 'initialTimeMs',
  'tensionState', 'selectedMechanic', 'selectedContentId', 'promptLines',
  'paused', 'pausedBy', 'countdownEndsAt', 'bombEndsAt',
  'returnWindowExpiresAt', 'returnWindowMsRemaining', 'voting', 'lastExplosionPlayerId',
  'tiebreakPlayers', 'winnerId', 'players', 'enabledMechanics', 'mixedMode',
  'baseDurationMs',
];

export const isGameStateSnapshot = (v: unknown): v is GameStateSnapshot => {
  if (v === null || typeof v !== 'object') return false;
  const obj = v as Record<string, unknown>;
  return REQUIRED_GAME_STATE_SNAPSHOT_FIELDS.every((f) => f in obj);
};

export interface ScoreEntry {
  playerId: PlayerId;
  displayName: string;
  totalMs: number;
  explosions: number;
}

export interface FinalResult {
  ranking: ScoreEntry[];
  winnerId: PlayerId | null;
  exactTie: boolean;
  tiedPlayerIds: PlayerId[];
  finished: boolean;
}

export interface ContentItemBase {
  id: ContentId;
  mechanic: Mechanic;
  enabled: boolean;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface CategoryContent extends ContentItemBase {
  mechanic: 'category';
  category: string;
}

export interface StartsWithLetterContent extends ContentItemBase {
  mechanic: 'starts_with_letter';
  letter: string;
}

export interface ContainsContent extends ContentItemBase {
  mechanic: 'contains';
  sequence: string;
}

export interface ExcludesLetterContent extends ContentItemBase {
  mechanic: 'excludes_letter';
  category: string;
  letter: string;
}

export interface ChainContent extends ContentItemBase {
  mechanic: 'chain';
  startWord: string;
}

export interface DualConditionContent extends ContentItemBase {
  mechanic: 'dual_condition';
  category: string;
  letter: string;
}

export interface NameThreeContent extends ContentItemBase {
  mechanic: 'name_three';
  category: string;
}

export type ContentItem =
  | CategoryContent
  | StartsWithLetterContent
  | ContainsContent
  | ExcludesLetterContent
  | ChainContent
  | DualConditionContent
  | NameThreeContent;

export interface ServerToClientEvents {
  'room:state': (state: GameStateSnapshot) => void;
  'room:error': (error: { code: string; message: string }) => void;
  'lobby:updated': (state: GameStateSnapshot) => void;
  'game:state': (state: GameStateSnapshot) => void;
  'game:exploded': (info: { playerId: PlayerId; displayName: string }) => void;
  'game:finished': (result: FinalResult) => void;
  'game:paused': (info: { byPlayerId: PlayerId | null }) => void;
  'game:resumed': (info: { byPlayerId: PlayerId | null }) => void;
  'player:left': (info: { playerId: PlayerId; displayName: string; reason: 'leave' | 'disconnect' }) => void;
  'player:reconnected': (info: { playerId: PlayerId; displayName: string }) => void;
  'voice:peer-enabled': (info: { playerId: PlayerId }) => void;
  'voice:peer-disabled': (info: { playerId: PlayerId }) => void;
  'voice:peer-speaking': (info: { playerId: PlayerId; speaking: boolean }) => void;
  'voice:rtp-capabilities': (caps: unknown) => void;
  'voice:transport-options': (
    info: { transportId: string; iceParameters: unknown; iceCandidates: unknown[]; dtlsParameters: unknown },
  ) => void;
  'voice:new-producer': (info: { playerId: PlayerId; producerId: string }) => void;
  'voice:producer-closed': (info: { playerId: PlayerId; producerId: string }) => void;
  'voice:consumer-options': (
    info: { consumerId: string; producerId: string; kind: 'audio'; rtpParameters: unknown },
  ) => void;
  'voice:error': (error: { code: string; message: string }) => void;
}

export interface ClientToServerEvents {
  'room:create': (
    payload: { displayName: string },
    ack: (response: { ok: true; roomId: RoomId; playerId: PlayerId } | { ok: false; error: string }) => void,
  ) => void;
  'room:join': (
    payload: { roomId: RoomId; displayName: string },
    ack: (
      response:
        | { ok: true; playerId: PlayerId; state: GameStateSnapshot }
        | { ok: false; error: string; reason?: string },
    ) => void,
  ) => void;
  'room:rejoin': (
    payload: { roomId: RoomId; playerId: PlayerId; displayName: string },
    ack: (
      response:
        | { ok: true; playerId: PlayerId; state: GameStateSnapshot }
        | { ok: false; error: string; reason?: string },
    ) => void,
  ) => void;
  'room:leave': () => void;
  'room:configure': (payload: { config: RoomConfig }) => void;
  'room:start': () => void;
  'game:pause': () => void;
  'game:resume': () => void;
  'bomb:pass': () => void;
  'bomb:return': () => void;
  'bomb:next': () => void;
  'vote:start': () => void;
  'vote:cast': (choice: VoteChoice) => void;
  'vote:decide': (target: ReturnTarget) => void;
  'vote:clear': () => void;
  'content:change': () => void;
  'player:remove': (payload: { playerId: PlayerId }) => void;
  'game:finish': () => void;
  'tiebreak:start': () => void;
  'voice:enable': () => void;
  'voice:disable': () => void;
  'voice:request-rtp-capabilities': (
    ack: (response: { ok: true; rtpCapabilities: unknown } | { ok: false; error: string }) => void,
  ) => void;
  'voice:create-transport': (
    payload: { direction: 'send' | 'recv' },
    ack: (
      response:
        | {
            ok: true;
            transportId: string;
            iceParameters: unknown;
            iceCandidates: unknown[];
            dtlsParameters: unknown;
          }
        | { ok: false; error: string },
    ) => void,
  ) => void;
  'voice:connect-transport': (
    payload: { transportId: string; direction: 'send' | 'recv'; dtlsParameters: unknown },
  ) => void;
  'voice:produce': (
    payload: { kind: 'audio'; rtpParameters: unknown },
    ack: (response: { ok: true; producerId: string } | { ok: false; error: string }) => void,
  ) => void;
  'voice:consume': (
    payload: { producerId: string; rtpCapabilities: unknown },
    ack: (
      response:
        | { ok: true; consumerId: string; producerId: string; kind: 'audio'; rtpParameters: unknown }
        | { ok: false; error: string },
    ) => void,
  ) => void;
  'voice:resume-consumer': (payload: { consumerId: string }) => void;
  'voice:close-producer': (payload: { producerId: string }) => void;
  'voice:speaking': (payload: { speaking: boolean }) => void;
}

export interface InterServerEvents {
  ping: () => void;
}

export interface SocketData {
  playerId: PlayerId;
  roomId: RoomId | null;
}
